// src/features/users/hooks/useUserPermissions.ts

import { useState, useEffect } from 'react';
import { usePermissions } from 'react-admin';
import keycloak from '@/lib/keycloak';
import { API_CONFIG } from '@/config/constants';
import type { UserRole } from '../types';

interface UserPermissions {
  canList: boolean;
  canShow: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canViewAll: boolean;
  canManagePasswords: boolean;
  canManageSuperAdmins: boolean;
}

/**
 * Hook to get user management permissions for the current user
 *
 * Business Rules:
 * - SuperAdmin: Full CRUD on all users
 * - Admin: Full CRUD except on SuperAdmin users (cannot assign SuperAdmin)
 * - GroupAdmin: CRUD on users of lower rank in their assigned groups
 * - HotelAdmin: NO user management (read-only removed)
 * - Viewer: NO access to user management
 * - canManageSuperAdmins: Only SuperAdmins in Keycloak group /staff/manager
 */
export const useUserPermissions = (): UserPermissions => {
  const { permissions } = usePermissions();

  // authProvider.getPermissions returns role directly as string
  const role = permissions as UserRole;

  // Strictly SuperAdmin — Admin can NEVER manage SuperAdmin users.
  const isSuperAdmin = role === 'SuperAdmin';
  const isElevated = role === 'SuperAdmin' || role === 'Admin';
  const isGroupAdmin = role === 'GroupAdmin';

  const [canManageSuperAdmins, setCanManageSuperAdmins] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin) {
      setCanManageSuperAdmins(false);
      return;
    }

    const fetchPermission = async () => {
      try {
        if (keycloak.isTokenExpired(30)) {
          await keycloak.updateToken(30);
        }
        const response = await fetch(`${API_CONFIG.BASE_URL}/permissions/me`, {
          headers: { Authorization: `Bearer ${keycloak.token}` },
        });
        const json = await response.json();
        setCanManageSuperAdmins(!!json?.data?.canManageSuperAdmins);
      } catch {
        setCanManageSuperAdmins(false);
      }
    };

    fetchPermission();
  }, [isSuperAdmin]);

  return {
    canList: isElevated || isGroupAdmin,
    canShow: isElevated || isGroupAdmin,
    canCreate: isElevated || isGroupAdmin,
    canEdit: isElevated || isGroupAdmin,
    canDelete: isElevated || isGroupAdmin,
    canViewAll: isElevated,
    canManagePasswords: isElevated || isGroupAdmin,
    canManageSuperAdmins,
  };
};
